/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path querybuilder
 * @description Query Builder interface implementation
 * @recursive true
 * @ignore __tests__
 */
import isDraftFilterAllowedAtLocation from "./../attributes/drafts/disabledPages";
import * as config from "./../configuration/index";
import { getElementCacheEntry } from "./ElementCache";
import QueryAttribute from "./QueryAttribute";
import { QueryAttributeList } from "./QueryAttributeList";
import { addQueryToBatch } from "./QueryBatchList";
import QueryPath from "./QueryPath";
import { QueryPathOptions } from "./QueryPathOptions";
import { QueryRecursionType } from "./QueryRecursionType";
import {
  failedCacheLog,
  infoCacheLog,
  infoCacheLogGreen,
  infoCacheLogReddish,
  infoCacheLogSubtle,
} from "./log";
// this is unique ID for each query
let queryId = 1;
/*
[SC.1838249806].subkpi
*/
export interface QueryInterface {
  Filter(options?: Partial<QueryPathOptions>): QueryInterface;
  parentobjects(options?: Partial<QueryPathOptions>): QueryInterface;
  subobjects(options?: Partial<QueryPathOptions>): QueryInterface;
  childobjects(options?: Partial<QueryPathOptions>): QueryInterface;
  measures(options?: Partial<QueryPathOptions>): QueryInterface;
  scorecard(options?: Partial<QueryPathOptions>): QueryInterface;
  KPI(options?: Partial<QueryPathOptions>): QueryInterface;
  PROJECT(options?: Partial<QueryPathOptions>): QueryInterface;
  customPath(path: string, options?: Partial<QueryPathOptions>): QueryInterface;
  models(options?: Partial<QueryPathOptions>): QueryInterface;
  BySymbol(options?: Partial<QueryPathOptions>): QueryInterface;
  topelement(options?: Partial<QueryPathOptions>): QueryInterface;
  series(options?: Partial<QueryPathOptions>): QueryInterface;
  valuesettings(options?: Partial<QueryPathOptions>): QueryInterface;
  period(options?: Partial<QueryPathOptions>): QueryInterface;
  periods(options?: Partial<QueryPathOptions>): QueryInterface;
  periodlevel(options?: Partial<QueryPathOptions>): QueryInterface;
  groupelements(options?: Partial<QueryPathOptions>): QueryInterface;
  attributes(...attributes: QueryAttributeList): QueryInterface;
  allowDraft(value: boolean): QueryInterface;
  sortBy(value: string): QueryInterface;
  fetchValues<T = any>(): Promise<T[]>;
}
class QueryBuilder implements QueryInterface {
  // unique ID for the query
  queryId: number;
  // if this query will replace other queries
  replacesQueries?: QueryBuilder[];
  // [SC.xxxxx]
  elementIds: string[];
  // [].<path>
  private _path: QueryPath[] = [];
  //
  _sortBy?: string;
  private _attributes: QueryAttribute[] = [];
  // eslint-disable-next-line
  resolve: (data: any) => void = () => null;
  // eslint-disable-next-line
  reject: (data: any) => void = () => null;
  private promise: Promise<any>;
  private hasBeenBatched: boolean = false;
  private allowDraftValues: boolean = false;
  constructor(elementId: null | undefined | string | string[]) {
    queryId += 1;
    this.queryId = queryId;
    this.promise = new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
    /**
     * Handle null, undefined or empty string here
     */
    if (
      typeof elementId === "undefined" ||
      elementId === null ||
      (typeof elementId === "string" && elementId.length === 0)
    ) {
      this.elementIds = [];
      return;
    }
    if (Array.isArray(elementId)) {
      this.elementIds = elementId;
    } else {
      this.elementIds = [elementId];
    }
    /**
     * Filter element ID values which are empty, null or undefined
     */
    this.elementIds = this.elementIds.filter((id) => id);
  }
  /**
   *
   * Resolves a simple query using cache or returns null
   *
   * @returns null or list of resolved elements
   */
  resolveQueryUsingCache(): null | any[] {
    try {
      /**
       * Handle simple recursive case, first calculate the recursion level from the parameters of the first
       * .childobjects path and then ask resolveElementsFromCache to resolve elements to a certain depth
       *
       * NOTE: the first element (root of the recursive query) should not be resolved
       */
      // TODO: figure out why this cache does not work correctly with reference values
      const recursion = this.getRecursionType();
      if (recursion.isRecursive && config.System.query.enableChildCache) {
        // get the [].childobjects Path element
        const path = this.getOnlyPath("childobjects");
        if (path) {
          let recursionLevel = 1;
          if (path.options?.recursive) {
            recursionLevel = path.options.recursive;
          }
          infoCacheLog(
            "Trying to resolve .childobjects query ",
            "recursion depth",
            recursionLevel
          );
          // when we have the recursion level, we can try to solve the child objects according to the
          // type of the elements queried
          return this.resolveElementsFromCache(
            this.elementIds,
            recursionLevel,
            true
          );
        }
        // cant solve this kind of query
        return null;
      }
      /**
       * For single element queries we can just check from the cache if the elements of the query have been
       * cached already
       */
      if (this.signature() === "[]" && config.System.query.enableObjectCache) {
        return this.resolveElementsFromCache(this.elementIds);
      }
      return null;
    } catch (e) {
      return null;
    }
  }
  /**
   *
   * Will transform the data from the cache to actual result objects
   *
   * @param elementIds list of elements to solve
   * @param recursionCount the depth of the recursion
   * @param childQueryRoot if this the first level of recursive query set to true
   * @returns
   */
  resolveElementsFromCache(
    elementIds: string[],
    recursionCount: number = 0,
    childQueryRoot = false
  ): null | Array<any> {
    try {
      // push results into this array
      let results: any[] = [];
      elementIds.forEach((elementId) => {
        const cachedEntry = getElementCacheEntry(elementId);
        if (!cachedEntry) {
          failedCacheLog(
            "Resolve failed at recursion level ",
            recursionCount,
            " because elementId",
            elementId,
            "was not cached"
          );
        }
        if (cachedEntry) {
          const allowDrafts = !isDraftFilterAllowedAtLocation();
          // resolve the item if it is not the childquery root element, which does not need to be resolved
          if (!childQueryRoot) {
            const solvedResult: any = { id: cachedEntry.id };
            const timeNow = Date.now();
            this.getAttributes()
              .filter((attr) => !attr.isDraft || allowDrafts)
              .forEach((attr) => {
                // get the attribute based on it's signature from the cache
                const solvable = cachedEntry.attributes[attr.signature()];
                if (solvable) {
                  if (solvable.expiresAt && solvable.expiresAt < timeNow) {
                    failedCacheLog(
                      "Resolve failed level: ",
                      recursionCount,
                      "elementId",
                      elementId,
                      " expired value for attribute ",
                      attr.signature()
                    );
                    throw new Error("unsolvable");
                  }
                  if (!solvable.isDraft || allowDrafts) {
                    solvedResult[attr.getName()] = solvable.cachedValue;
                  }
                } else {
                  failedCacheLog(
                    "Resolve failed  level: ",
                    recursionCount,
                    "elementId",
                    elementId,
                    " not cached attr: ",
                    attr.signature()
                  );
                  throw new Error("unsolvable");
                }
              });
            const mappedObj = config.System.mapQueryResults(solvedResult);
            results.push(mappedObj);
          }
          /**
           * Handle recursive case, in case we have something like children(recursive=3)
           * we may look for the cache for fetched child objects
           */
          if (recursionCount > 0) {
            const type = this.getRecursionType();
            if (!cachedEntry.children && !type.hasFilter) {
              failedCacheLog(
                "Resolve failed  level: ",
                recursionCount,
                "elementId",
                elementId,
                "no children in the cache"
              );
              throw new Error("unsolvable");
            }
            /**
             * If there is filter but the childrenOfType does not have the children set for this type we fail
             */
            if (
              type.hasFilter &&
              (!cachedEntry.childrenOfType ||
                !cachedEntry.childrenOfType[type.filterType || ""])
            ) {
              failedCacheLog(
                "Resolve failed  level: ",
                recursionCount,
                "elementId",
                elementId,
                "no typed childen in the cache for type",
                type.filterType
              );
              throw new Error("unsolvable");
            }
            /**
             * If no filter and children is set try to solve the children
             */
            if (!type.hasFilter && cachedEntry.children) {
              // fetch children too
              const newList = this.resolveElementsFromCache(
                Object.keys(cachedEntry.children),
                recursionCount - 1
              );
              if (newList === null) {
                throw new Error("unsolvable");
              }
              const mappedObjList = newList.map((item) =>
                config.System.mapQueryResults(item)
              );
              results = results.concat(mappedObjList);
              return;
            }
            /**
             * If there is filter and cache is ready for the type of elements in the filter, try to solve them
             */
            if (
              type.hasFilter &&
              cachedEntry.childrenOfType &&
              typeof type.filterType === "string" &&
              cachedEntry.childrenOfType[type.filterType]
            ) {
              // fetch children too
              const newList = this.resolveElementsFromCache(
                Object.keys(cachedEntry.childrenOfType[type.filterType]),
                recursionCount - 1
              );
              if (newList === null) {
                throw new Error("unsolvable");
              }
              const mappedObjList = newList.map((item) =>
                config.System.mapQueryResults(item)
              );
              results = results.concat(mappedObjList);
              return;
            }
            /**
             * if none of the valid paths returned a value we fail, just in case we get here
             */
            failedCacheLog(
              "Resolve failed  level: ",
              recursionCount,
              "elementId",
              elementId,
              "no typed childen in the cache for type",
              type.filterType
            );
            throw new Error("unsolvable");
          }
          return; // exit the for loop
        }
        throw new Error("unsolvable");
      });
      return results;
    } catch (e) {
      return null;
    }
  }
  /**
   *
   * Checks the special case: if there is only one path in the query which has only one path element
   * of some specific type, in this case the default is "childobjects"
   *
   * @param ofType
   * @returns
   */
  getOnlyPath(ofType: "childobjects") {
    const path = this.getPath();
    if (path.length === 1) {
      if (path[0].tag === ofType) {
        return path[0];
      }
    }
    return false;
  }
  /**
   * Determines if the query has a simple recursion type which can be handled by the
   * query optimizer and cache.
   *
   * @returns QueryRecursionType
   */
  getRecursionType(): QueryRecursionType {
    const path = this.getPath();
    if (path.length === 1) {
      if (path[0].tag === "childobjects") {
        const childPath = path[0];
        let depth = childPath.options?.recursive || 1;
        // if no options then no filters and can be resolved
        if (!childPath.options) {
          return {
            hasFilter: false,
            isRecursive: true,
            depth,
          };
        }
        /**
         * Only recursive and criteria are supported in optimization
         */
        const notSuppotedOptions = Object.keys(childPath.options).filter(
          (opt) => {
            return !["recursive", "criteria"].includes(opt);
          }
        );
        if (notSuppotedOptions.length > 0) {
          return {
            isRecursive: false,
            hasFilter: false,
          };
        }
        /**
         * If the criteria is a free form string we can not parse it
         */
        if (typeof childPath.options?.criteria === "string") {
          return {
            isRecursive: false,
            hasFilter: false,
          };
        }
        if (typeof childPath.options?.criteria === "object") {
          /**
           * If the criteria has anything else than the "typename" we can don't know how to handle it
           */
          const notSuppotedOptions = Object.keys(
            childPath.options?.criteria
          ).filter((opt) => {
            return !["typename"].includes(opt);
          });
          if (notSuppotedOptions.length > 0) {
            return {
              isRecursive: false,
              hasFilter: false,
            };
          }
          /**
           * If there is no "typename", criteria is empty and filter does not exist
           */
          if (!childPath.options?.criteria.typename) {
            return {
              hasFilter: false,
              isRecursive: true,
              depth,
            };
          }
          return {
            isRecursive: true,
            hasFilter: true,
            filterType: childPath.options?.criteria.typename,
            depth,
          };
        }
        /**
         * only "recursive" used
         */
        return {
          hasFilter: false,
          isRecursive: true,
          depth,
        };
      }
    }
    return {
      isRecursive: false,
      hasFilter: false,
    };
  }
  getPeriodStatus(resultObj: any): Record<string, string> {
    const statusPeriods: Record<string, string> = {};
    this.getAttributes().forEach((attr) => {
      if (attr.options?.series === "STATUS" && attr.options.period) {
        statusPeriods[attr.options.period] = resultObj[attr.getQueryName()];
      }
    });
    return statusPeriods;
  }
  markDraftResults(resultObj: any): any {
    /*
        const attrMap: Record<string, QueryAttribute> = {};
    
        let typeName = '';
        this._attributes.forEach(attr => {
          if (attr.options?.period) {
            attrMap[attr.getName()] = attr;
          }
          if (attr.getName() === 'typename') {
            typeName = resultObj[attr.getQueryName()];
          }
        });
        if (!typeName) {
          return;
        }
    
        this._attributes.forEach(attr => {
          const filtered = config.System.draftValuesHandling.getListOfDraftFilteredSeries(typeName);
          // const filtered = getListOfDraftFilteredSeries(typeName);
          if (
            filtered.length > 0 &&
            attr.options?.period &&
            (!attr.options.series || (attr.options.series && filtered.includes(attr.options.series)))
          ) {
            // attr.
            const statusAttr = attrMap[`status_${getInternalPeriodId(attr.options?.period)}`];
            if (statusAttr) {
              const resV = resultObj[statusAttr.getQueryName()];
    
              // strict draft
              if (config.System.draftValuesHandling.isDraftStatus(resV)) {
                // if (resV !== '1') {
                attr.isDraft = true;
              }
            }
          }
        });
        */
  }
  getTypeName(resultObj: any) {
    const typeNameAttr = this.getAttributes()
      .filter((attr) => attr.getName() === "typename")
      .pop();
    const typeName: string | undefined =
      resultObj[typeNameAttr?.getQueryName() || ""];
    return typeName;
  }
  getDraftSeries(resultObj: any) {
    const typeName = this.getTypeName(resultObj);
    const draftSeries = typeName
      ? config.System.draftValuesHandling.getListOfDraftFilteredSeries(typeName)
      : [];
    return draftSeries;
  }
  /**
   *
   * This function transforms the MEA query results to the names of the original query,
   *
   * For example if the query attribute was
   * prettyvalue(series=\"BUDGET\",period=\"Q4 / 2021\",as=\"budget_9\")
   *
   * The property "budget_9" transformed to "budget" in the resultset
   *
   * @param resultObj
   * @returns
   */
  transformResultObject(resultObj: any): any {
    const obj: any = {};
    const idWasAsked =
      this._attributes.filter((a) => a.getName() === "id").length > 0;
    if (idWasAsked && resultObj._original_id) {
      obj.id = resultObj._original_id;
    } else {
      obj.id = resultObj.id;
    }
    const allowDraft =
      this.allowDraftValues || !isDraftFilterAllowedAtLocation();
    const statusPeriods = this.getPeriodStatus(resultObj);
    const draftSeries = this.getDraftSeries(resultObj);
    this._attributes
      .filter((attr) => {
        if (allowDraft || !attr.options?.period || draftSeries.length === 0)
          return true;
        const isDrafSeries = draftSeries.find(
          (ser) =>
            ser.toLocaleLowerCase() === attr.getName().toLocaleLowerCase()
        );
        if (!isDrafSeries) return true;
        const status = statusPeriods[attr.options?.period];
        return !config.System.draftValuesHandling.isDraftStatus(status);
      })
      .filter((attr) => attr.getName() !== "id")
      .forEach((attr) => {
        obj[attr.getName()] = resultObj[attr.getQueryName()];
      });
    return config.System.mapQueryResults(obj);
  }
  /**
   *
   * Will return promise which will hold the values of the query after the Promise has been resolved
   *
   * @returns
   */
  fetchValues() {
    if (!this.hasBeenBatched) {
      addQueryToBatch(this);
    }
    this.hasBeenBatched = true;
    return this.promise;
  }
  /**
   * Removes duplicates manually, however, this function is not actually used anywhere and
   * is a candidate to be removed. This function should not be needed really
   */
  removeDuplicateAttributes() {
    const attributes: Record<string, QueryAttribute> = {};
    this.getAttributes().forEach((attr) => {
      // check duplicates for the query
      if (!attributes[attr.signature()]) {
        attributes[attr.signature()] = attr;
      }
    });
    this._attributes = Object.entries(attributes).map(([name, value]) => {
      return value;
    });
  }
  /**
   * Creates the "query" -part for the meaquery endpoint
   *
   * @param elementIds list of ID's to use for this query
   * @returns string representing the query to be sent to MEA
   */
  toMeaQuery(elementIds?: string[]) {
    const ids = [...new Set(elementIds?.filter((id) => id) || this.elementIds)];
    return `[${ids.join(",")}]${this._path.length > 0 ? "." : ""}${this._path
      .map((pathItem) => {
        return pathItem.toString();
      })
      .join(".")}`;
  }
  /**
   *
   * Get the attributes for the MEA query
   *
   * @returns string presenting the attributes part of MEA query
   */
  toMeaAttributes() {
    // referenceid(as="_original_id")
    // do not ask the id
    const list = this._attributes
      .filter((attr) => attr.getName() !== "id")
      .map((attr) => {
        return attr.build();
      });
    list.push('referenceid(as="_original_id")');
    return `${list.join(",")}`;
  }
  /**
   * Signature of the query
   *
   * @returns string signature of the query part
   */
  signature() {
    return this.toMeaQuery([]);
  }
  /**
   *
   * Identify identical queries
   *
   * @returns string signature
   */
  fullQuerySignature() {
    return `${this.toMeaQuery(this.elementIds)}::${this._attributes
      .map((attr) => attr.signature(true))
      .join(",")}`;
  }
  /**
   *
   * Logs the query to the console using colors
   *
   * @param style
   * @returns
   */
  prettyLogQuery(style?: string) {
    if (style === "red") {
      infoCacheLogReddish("QUERY: ", this.toMeaQuery(this.elementIds));
      infoCacheLogReddish("Attributes: ", this.toMeaAttributes());
      return;
    }
    if (style === "green") {
      infoCacheLogGreen("QUERY: ", this.toMeaQuery(this.elementIds));
      infoCacheLogGreen("Attributes: ", this.toMeaAttributes());
      return;
    }
    infoCacheLogSubtle("QUERY: ", this.toMeaQuery(this.elementIds));
    infoCacheLogSubtle("Attributes: ", this.toMeaAttributes());
  }
  /**
   *
   * The function will check if the attribute already exists in the query. In case user tries
   * to add different attribute with the same name (the "as=...") will throw Error
   *
   * @param attr
   * @returns true, false or throws exception on error
   */
  hasAlreadyAttribute(attr: QueryAttribute, doNotThrow = false) {
    for (let i = 0; i < this._attributes.length; i += 1) {
      const a = this._attributes[i];
      // if exactly the same, then just skip adding
      if (a.tag === attr.tag && a.signature(true) === attr.signature(true))
        return true;
      // if different but trying to assign to the same name, raise exception
      if (a.signature() !== attr.signature()) {
        if (a.options && attr.options) {
          if (
            a.options.as &&
            attr.options.as &&
            a.options.as === attr.options.as
          ) {
            if (doNotThrow) {
              return true;
            }
            throw new Error(
              `Duplicate attribute ${
                attr.options.as
              } in query ${this.toMeaQuery()}`
            );
          }
        }
      }
    }
    return false;
  }
  /**
   * Returns the attributes for the query
   *
   * @returns list of QueryAttribute objects
   */
  getAttributes() {
    return this._attributes;
  }
  /**
   * Returns the path in the query part of the query
   *
   * @returns list of QueryPath objects
   */
  getPath() {
    return this._path;
  }
  /**
   *
   * Manually set attributes for the query
   *
   * @param list
   * @returns
   */
  setAttributes(list: QueryAttribute[]) {
    this._attributes = list;
    return this;
  }
  /**
   *
   * Not used right now. TODO: implement this
   *
   * @param field
   */
  sortBy(field: string) {
    this._sortBy = field;
    return this;
  }
  /**
   * Path "Filter" for the query
   *
   *
   * @param options QueryPathOptions possible options to the filter
   * @returns
   */
  Filter(options?: Partial<QueryPathOptions>) {
    this._path.push(new QueryPath("Filter", options));
    return this;
  }
  /**
   * Path "parentobjects" for the query
   *
   * @param options
   * @returns
   */
  parentobjects(options?: Partial<QueryPathOptions>) {
    this._path.push(new QueryPath("parentobjects", options));
    return this;
  }
  /**
   * Path "childobjects" for the query
   *
   * @param options
   * @returns
   */
  childobjects(options?: Partial<QueryPathOptions>) {
    this._path.push(new QueryPath("childobjects", options));
    return this;
  }
  /**
   * Path "subobjects" for the query
   *
   * @param options
   * @returns
   */
  subobjects(options?: Partial<QueryPathOptions>) {
    this._path.push(new QueryPath("subobjects", options));
    return this;
  }
  /**
   * Path "scorecard" for the query
   *
   * @param options
   * @returns
   */
  scorecard(options?: Partial<QueryPathOptions>) {
    this._path.push(new QueryPath("scorecard", options));
    return this;
  }
  /**
   * Path "KPI" for the query
   *
   * @param options
   * @returns
   */
  KPI(options?: Partial<QueryPathOptions>) {
    this._path.push(new QueryPath("KPI", options));
    return this;
  }
  /**
   * Path "project" for the query
   *
   * @param options
   * @returns
   */
  PROJECT(options?: Partial<QueryPathOptions>) {
    this._path.push(new QueryPath("project", options));
    return this;
  }
  /**
   *
   *
   * @param customPath
   * @param options
   * @returns
   */
  customPath(path: string, options?: Partial<QueryPathOptions>) {
    this._path.push(new QueryPath(path, options));
    return this;
  }
  /**
   * Path "series" for the query
   *
   * @param options
   * @returns
   */
  series(options?: Partial<QueryPathOptions>) {
    this._path.push(new QueryPath("series", options));
    return this;
  }
  /**
   * Path "valuesettings" for the query
   *
   * @param options
   * @returns
   */
  valuesettings(options?: Partial<QueryPathOptions>) {
    this._path.push(new QueryPath("valuesettings", options));
    return this;
  }
  /**
   * Path "model" for the query
   *
   * @param options
   * @returns
   */
  models(options?: Partial<QueryPathOptions>) {
    this._path.push(new QueryPath("models", options));
    return this;
  }
  /**
   * Path "model" for the query
   *
   * @param options
   * @returns
   */
  BySymbol(options?: Partial<QueryPathOptions>) {
    this._path.push(new QueryPath("BySymbol", options));
    return this;
  }
  /**
   * Path "topelement" for the query
   *
   * @param options
   * @returns
   */
  topelement(options?: Partial<QueryPathOptions>) {
    this._path.push(new QueryPath("topelement", options));
    return this;
  }
  /**
   * Path "periodlevel" for the query
   *
   * @param options
   * @returns
   */
  periodlevel(options?: Partial<QueryPathOptions>) {
    this._path.push(new QueryPath("periodlevel", options));
    return this;
  }
  /**
   * Path "periodlevel" for the query
   *
   * @param options
   * @returns
   */
  period(options?: Partial<QueryPathOptions>) {
    this._path.push(new QueryPath("period", options));
    return this;
  }
  /**
   * Path "periodlevel" for the query
   *
   * @param options
   * @returns
   */
  periods(options?: Partial<QueryPathOptions>) {
    this._path.push(new QueryPath("periods", options));
    return this;
  }
  /**
   *
   * Path "measures" for the query
   *
   * @param options
   * @returns
   */
  measures(options?: Partial<QueryPathOptions>) {
    this._path.push(new QueryPath("measures", options));
    return this;
  }
  /**
   *
   * Path "groupelements" for for the MEA query
   *
   * @param options
   * @returns
   */
  groupelements(options?: Partial<QueryPathOptions>) {
    this._path.push(new QueryPath("groupelements", options));
    return this;
  }
  /**
   *
   * Define the attributes for the query
   *
   * [SC.xxx.yyy].attributes('name', 'typename', ['value', {period:'2021'}])
   *
   * @param options
   * @returns
   */
  attributes(...attributes: QueryAttributeList) {
    attributes.forEach((value) => {
      if (typeof value === "string") {
        const a = new QueryAttribute(value, null);
        if (!this.hasAlreadyAttribute(a)) {
          this._attributes.push(a);
        }
      } else {
        const [name, options] = value;
        // if attribute is asked for a certain period, then ask also the status flag
        const a = new QueryAttribute(name, options);
        if (!this.hasAlreadyAttribute(a)) {
          this._attributes.push(a);
          /**
           * status_xxxxx here would be indicative attribute which could be filtering out
           * the value if status is set to '0'
           */
          if (options.period) {
            const statusAttr = new QueryAttribute("value", {
              series: "STATUS",
              period: options.period,
              as: `STATUS`,
            });
            if (!this.hasAlreadyAttribute(statusAttr, true)) {
              this._attributes.push(statusAttr);
            }
          }
        }
      }
    });
    return this;
  }
  /**
   * Path "periodlevel" for the query
   *
   * @param options
   * @returns
   */
  allowDraft(value: boolean = true) {
    if (value) {
      this.allowDraftValues = value;
    }
    return this;
  }
}
export default QueryBuilder;
